import React from "react";
import { submitMedikreditClaim } from "../../mediKreditapi";

const pad = (n) => String(n).padStart(2, "0");

function formatDateYYYYMMDD(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).slice(0, 10).replace(/-/g, "");
}

// Local time → "HHMMSS" (no UTC conversion)
function toHHMMSSLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${hh}${mm}${ss}`;
}

function extractDatesAndTimes(line, fallbackInvoiceDate) {
  const startISO = line.appointmentCustomStart || line.appointmentStartSet || null;
  const endISO   = line.appointmentCustomEnd   || line.appointmentEndSet   || null;
  const dateOnly = line.appointmentDateCustom || line.appointmentDateSet || fallbackInvoiceDate || "";

  let stDate = "", endDate = "", stTime = "", endTime = "";

  if (startISO || endISO) {
    const s = startISO ? new Date(startISO) : null;
    const e = endISO ? new Date(endISO) : null;

    stDate  = s ? startISO.slice(0, 10) : (dateOnly || "");
    endDate = e ? endISO.slice(0, 10)   : (stDate || dateOnly || "");

    stTime  = s ? toHHMMSSLocal(startISO) : "000000";
    endTime = e ? toHHMMSSLocal(endISO)   : stTime || "000000";
  } else if (dateOnly) {
    stDate = endDate = dateOnly.slice(0, 10);
    stTime = "000000";
    endTime = "000000";
  }

  return {
    st_dt: formatDateYYYYMMDD(stDate),
    st_tm: stTime,
    end_dt: formatDateYYYYMMDD(endDate),
    end_tm: endTime,
  };
}

function parseCodeListMaybeSemicolon(strOrArr) {
  if (Array.isArray(strOrArr)) return strOrArr.filter(Boolean);
  if (!strOrArr) return [];
  return String(strOrArr)
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
}

function getCd1Array(line) {
  // Prefer explicit arrays if present
  if (Array.isArray(line.icd10Primary)) return line.icd10Primary.filter(Boolean);
  if (Array.isArray(line.icd10Primaries)) return line.icd10Primaries.filter(Boolean);
  if (Array.isArray(line.icd101)) return line.icd101.filter(Boolean);

  // Back-compat: single string or semi-colon list from icd101
  if (typeof line.icd101 === "string") {
    return parseCodeListMaybeSemicolon(line.icd101);
  }
  return [];
}

function getCd2Array(line) {
  if (Array.isArray(line.icd10Secondary)) return line.icd10Secondary.filter(Boolean);
  if (Array.isArray(line.icd102)) return line.icd102.filter(Boolean);
  if (line.icd102) return parseCodeListMaybeSemicolon(line.icd102);
  return [];
}

export async function SubmitClaim({
  practice,
  account,
  selectedPatient,
  invoice,
  claimtxn,
}) {
  const practiceInfo = {
    sp_bhf: practice.practiceNumber,
    sp_hpc: invoice.practitioner.hpcsa,
    grp_prac: practice.practiceNumber,
    tx_nbr: claimtxn,
    msg_fmt: "13",
    tx_cd: "21",
  };

  const medicalAidInfo = {
    plan: selectedPatient.patientMediKreditPlanNumber03,
    ch_id: selectedPatient.patientMedicalAidNumber,
    sname: selectedPatient.patientLastName,
    inits: selectedPatient.patientInitials,
  };

  const financialInfo = {
    totalMedAidAmount: invoice.totalMedicalAidAmount,
  };

  const patientInfo = {
    dep_cd: selectedPatient.patientMedicalAidDependantCode,
    dob: formatDateYYYYMMDD(selectedPatient.patientDOB),
    id_nbr: selectedPatient.patientIdNumber,
    fname: selectedPatient.patientFirstName,
    gend: selectedPatient.patientGender,
    rlnship: selectedPatient.patientRelationship,
    status: invoice.outPatient, // "O" or "I"
    int: selectedPatient.patientInitials,
  };

  // --- NEW: Case info comes from invoice.case (not inPatientCase) ---
  const casePrimaries =
    Array.isArray(invoice.case?.icd101)
      ? invoice.case.icd101.filter(Boolean)
      : invoice.case?.icd101
      ? [invoice.case.icd101]
      : [];
  const caseSecondaries =
    Array.isArray(invoice.case?.icd102) ? invoice.case.icd102.filter(Boolean) : [];

  const caseInfo = {
    ref_bhf: invoice.case?.referringDoctorBHF || "",
    ref_hpcsa: invoice.case?.referringDoctorHPCSA || "",
    auth_nbr: invoice.case?.authorizationNumber || "",
    auth_by: invoice.case?.authorizedBy || "",
    cd1: casePrimaries,   // array of primaries (stg_01)
    cd2: caseSecondaries, // array of secondaries (stg_02)
  };

  // Build transaction lines
  let currentLineNumber = 1;
  const transactionInfo = [];
  const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
  const fallbackInvoiceDate = invoice.invoiceDate || "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const lineDescription = String(line.description ?? "");
    const lineTariffCode = String(line.tariffCode ?? "");
    const lineQuantity = Number(line.quantity ?? 1);

    const cd1Array = getCd1Array(line); // primaries array
    const cd2Array = getCd2Array(line); // secondaries array

    // Times: exact local start/end from the line
    const { st_dt, st_tm, end_dt, end_tm } = extractDatesAndTimes(line, fallbackInvoiceDate);

    // Use case authorization number for all lines
    const lineAuthNumber = caseInfo.auth_nbr || "";

    // Normalise modifiers to array
    let modifiers = [];
    if (line.modifier) {
      modifiers = Array.isArray(line.modifier) ? line.modifier.filter(Boolean) : [line.modifier];
    }

    const hasNegativeModifier = modifiers.some((m) =>
      typeof m === "object" ? Number(m.rateChange) < 0 : false
    );

    const basePrice = hasNegativeModifier
      ? Number(line.medAidAmount ?? 0)
      : Number(line.originalRate ?? line.medAidAmount ?? 0);

    // Primary service line (tp="2")
    const baseLine = {
      lin_num: currentLineNumber++,
      pay_adv: "P",
      tp: "2",
      cdg_set: "02",
      auth_nbr: lineAuthNumber,
      descr: lineDescription,
      process_tp: "01",
      tar_cd: lineTariffCode,
      qty: "1",
      units: lineQuantity,
      unit_tp: "06",
      st_dt,
      st_tm,
      end_dt,
      end_tm,
      // ICD arrays
      cd1: cd1Array, // primary ICD-10s
      cd2: cd2Array, // secondary ICD-10s
      tp_ind: "01",
      bhf_nbr: practiceInfo.sp_bhf,
      hpc_nbr: invoice.practitioner.hpcsa,
      tar_ind: "03",
      price: basePrice,
      gross: basePrice,
      bs_of_cst: "3",
      sp_hpc: invoice.practitioner.hpcsa,
      oti: line.oti || "A",
    };
    transactionInfo.push(baseLine);

    // Modifier lines (tp="3")
    for (let j = 0; j < modifiers.length; j++) {
      const modifier = modifiers[j];
      const rateChange = typeof modifier === "object" ? Number(modifier.rateChange ?? 0) : 0;
      const modCode = typeof modifier === "object" ? modifier.code : String(modifier ?? "");
      const isPositive = rateChange > 0;

      transactionInfo.push({
        lin_num: currentLineNumber++,
        pay_adv: "P",
        tp: "3",
        cdg_set: "02",
        auth_nbr: lineAuthNumber,
        descr: lineDescription,
        process_tp: "01",
        tar_cd: lineTariffCode,
        qty: "1",
        units: lineQuantity,
        unit_tp: "06",
        st_dt,
        st_tm,
        end_dt,
        end_tm,
        cd1: cd1Array, // use same ICD coding as base
        cd2: cd2Array,
        tp_ind: "01",
        bhf_nbr: practiceInfo.sp_bhf,
        hpc_nbr: invoice.practitioner.hpcsa,
        tar_ind: "03",
        price: isPositive ? rateChange : 0,
        gross: isPositive ? rateChange : 0,
        bs_of_cst: "3",
        sp_hpc: invoice.practitioner.hpcsa,
        modtp: "3",
        mod: modCode,
        oti: line.oti || "A",
      });
    }
  }

  try {
    const response = await submitMedikreditClaim(
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo
    );

    console.log("Claim Submission Data:", {
      practiceInfo,
      medicalAidInfo,
      patientInfo,
      transactionInfo,
      financialInfo,
      caseInfo,
    });

    console.log("✅ Claim Response:", response);
    return response;
  } catch (error) {
    console.error("❌ Claim Submission Failed:", error);
    throw error;
  }
}
